import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

// Actions - based on existing patterns from HistoryList_V2 and PendingList_V2
import {
  SG_GET_DEBRIEF_SCHEDULES,
  ADD_ACTIVE_PAGE_DEBRIEF_SCHEDULE,
  SG_DELETE_DEBRIEF_SCHEDULE,
  SG_GET_CULTURE_AUDIT_REPORTS,
  SG_GET_SURVEY_QUESTIONS,
  SG_GET_SURVEY_STRUCTURES,
  SG_ADD_ORGANIZATION_GROUP,
  SG_BULK_UPDATE_ORGANIZATION_GROUP,
  SG_ADD_DEBRIEF_SCHEDULE,
  SG_ADD_DEBRIEF_SCHEDULE_SUCCESS,
  SG_ADD_DEBRIEF_SCHEDULE_FAIL,
  SG_GET_ORGANIZATION_GROUP,
  SG_GET_DEBRIEF_MEMBERS,
} from "constants/actions";

import {
  SURVEY_TYPE,
  SURVEY_INFO,
  SURVEY_QUESTION_TYPE,
} from "constants/surveys";

const formatUpdatedDisplayDate = (dateString) => {
  if (!dateString) return null;
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return dateString;
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Create the Schedule Context
const ScheduleContext = createContext();

// Custom hook to use the schedule context
export const useScheduleContext = () => {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error(
      "useScheduleContext must be used within a ScheduleProvider"
    );
  }
  return context;
};

// Schedule Provider Component
export const ScheduleProvider = ({ children }) => {
  const dispatch = useDispatch();

  // Local state management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [fetchAgain, setFetchAgain] = useState(false);
  const [deletedRecord, setDeletedRecord] = useState(false);

  // Organization and user data
  const [organizationId, setOrganizationId] = useState(null);
  const [userAdmin, setUserAdmin] = useState(false);
  const [userEmployee, setUserEmployee] = useState(null);
  const [userEmployeeCategoryList] = useState([]);

  // Survey structure data
  const [primaryCategory, setPrimaryCategory] = useState([]);
  const [secondaryCategory, setSecondaryCategory] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [primarySecondaryName, setPrimarySecondaryName] = useState({
    primary: "Type",
    secondary: "Role",
  });
  const [selectedSurveyQuestion, setSelectedSurveyQuestion] = useState(null);

  // Pagination settings
  const defaultPageSize = 12;

  // Redux selectors - following the same pattern as existing files
  const {
    get_auth,
    get_organizations,
    get_employee,
    get_survey_structure,
    get_survey_questions,
    get_debrief_schedules,
    get_culture_audit_reports,
    organization_group,
    organization_groups_list,
    organization_groups_page,
    employees,
    debrief_members_by_id
  } = useSelector(
    (state) => ({
      debrief_members_by_id: state.debrief_schedule?.debrief_members_by_id,
      get_auth: state.auth,
      get_organizations: state.organizations,
      get_employee: state.employees,
      get_survey_structure: state.surveystructure?.survey_structure,
      get_survey_questions: state.surveyquestions?.survey_questions,
      get_debrief_schedules: state.debrief_schedule,
      get_culture_audit_reports: state.debrief_schedule?.culture_audit_reports,
      organization_group: state.organization_group?.organization_group,
      organization_groups_list: state.organization_group?.organization_groups,
      organization_groups_page: state.organization_group?.page,
      employees: state.employees?.employees,
    }),
    shallowEqual
    );

  // Watch organization group state for success signals
  const stateOrgGroup = useSelector((s) => s.organization_group, shallowEqual);

  // Track last created debrief schedule
  const [lastScheduleCreationTime, setLastScheduleCreationTime] = useState(null);

  // Cache of groupId -> member IDs[] and lastUpdated timestamp
  const [groupMembersById, setGroupMembersById] = useState({}); // { [id]: { members: number[], updatedAt: number } }
  // Quick-lookup: groupId -> members_count (from SG_GET_ORGANIZATION_GROUP list)
  const [membersCountByGroupId, setMembersCountByGroupId] = useState({}); // { [groupId]: number }
  // Fetch all survey groups for this organization once (to get members_count efficiently)
  useEffect(() => {
    if (!organizationId) return;
    // We fetch the first page with a generous page_size; backend caps if needed
    // Prefer newest groups first so newly created groups are visible immediately
    // DRF-style ordering is commonly supported via `ordering` param; fall back to client sort elsewhere if unsupported.
    const query = `?type=survey&organization=${organizationId}&page=1&page_size=500`;

    dispatch({ type: SG_GET_ORGANIZATION_GROUP, payload: query });
  }, [dispatch, organizationId]);

  // Build a fast map: groupId -> members_count whenever list updates
  useEffect(() => {
    const list = Array.isArray(organization_groups_list) ? organization_groups_list : [];
    if (list.length === 0) return;
    const next = {};
    for (const g of list) {
      if (g && typeof g.id === 'number') {
        next[g.id] = typeof g.members_count === 'number' ? g.members_count : 0;
      }
    }
    setMembersCountByGroupId(next);
  }, [organization_groups_list]);

  // When a single group arrives/updates in Redux, cache its members locally for quick lookup
  useEffect(() => {
    const g = organization_group;
    if (!g || !g.id) return;

    const members = Array.isArray(g.members)
      ? g.members.map(m => (typeof m === 'number' ? m : m?.id)).filter(Boolean)
      : [];

    setGroupMembersById(prev => ({
      ...prev,
      [g.id]: { members, updatedAt: Date.now() }
    }));
  }, [organization_group]);

  // Given a schedule (from debrief_schedules results), return respondent employee objects
  const getRespondentsForSchedule = (schedule) => {
    if (!schedule) return [];
    const schedId = schedule.id;
    const bucket = debrief_members_by_id?.[schedId];
    const members = Array.isArray(bucket?.results) ? bucket.results : [];
    // Map to global employee objects by id if `employee_id` exists; otherwise return raw member rows
    const list = Array.isArray(employees) ? employees : [];
    const byId = new Map(list.map(e => [e.id, e]));
    const mapped = members.map(m => byId.get(m.employee_id || m.id)).filter(Boolean);
    return mapped.length > 0 ? mapped : members;
  };

  const ambassadorId = useSelector(
    (state) => Number(state.auth?.ambassador_id),
    shallowEqual
  );

  // Orchestrated scheduling flow state
  const [scheduleFlow, setScheduleFlow] = useState({
    active: false, // are we currently running the flow
    step: "idle", // 'idle' | 'creatingGroup' | 'waitingBulk' | 'waitingSchedule' | 'done' | 'error'
    createdGroupId: null, // captured after group creation
    error: null, // last error (if any)
    temp: null, // { formData, selectedEmployees, groupName, recipientMode }
  });

  // Map UI surveyType string to backend enum (kept in sync with ScheduleSurvey)
  const mapTypeOf = (str) => {
    switch (str) {
      case "culture_audit":
        return SURVEY_TYPE?.AUDIT ?? SURVEY_TYPE?.AUDIT_ASSESSMENT ?? 1;
      case "pulse_survey":
        return SURVEY_TYPE?.PULSE ?? SURVEY_TYPE?.PULSE_SURVEY ?? 2;
      case "360_review":
        return SURVEY_TYPE?.REVIEW_360 ?? SURVEY_TYPE?.REVIEW360 ?? 3;
      case "edi_survey":
        return SURVEY_TYPE?.EDI ?? SURVEY_TYPE?.EDI_SURVEY ?? 4;
      case "custom":
      default:
        return SURVEY_TYPE?.AUDIT ?? 1;
    }
  };

  // Build the final schedule payload; attach groupId when provided
  const buildSchedulePayload = useCallback(({ formData, groupId }) => {
    const toLocalDate = (date, time) => {
      if (!date || !time) return null;
      const d = new Date(`${date}T${time}`);
      return d.toISOString();
    };

    const startIso = toLocalDate(formData.startDate, formData.startTime);
    const endIso = toLocalDate(formData.endDate, formData.endTime);
    const scheduleName = formData?.name?.trim() || "";

    return {
      user_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      type_of: mapTypeOf(formData?.surveyType),
      scheduled_for: startIso,
      time_zone: formData.timezone,
      name: scheduleName,
      close_at: endIso,
      organization: Number(organizationId),
      survey_structure:
         Array.isArray(get_survey_structure) && get_survey_structure[0]?.id
        ? get_survey_structure[0].id
        : null,
      brief_question: selectedSurveyQuestion?.id || null,
      include_members: !!formData.createPublicLink,
      require_id: !!formData.useEmployeeIds,
      name_uid: "95cd36d1-028b-4314-bf24-49fe90a9f1d8",
      ...(groupId ? { organization_group: groupId } : {}),
    };
  }, [organizationId, get_survey_structure, selectedSurveyQuestion]);

  // Initialize organization ID
  useEffect(() => {
    if (get_auth?.organization_id) {
      setOrganizationId(Number(get_auth.organization_id));
    }
  }, [get_auth]);

  // Initialize user data
  useEffect(() => {
    setUserAdmin(false);
    if (get_employee) {
      setUserEmployee(get_employee?.userEmp);
      if (get_employee.userEmp?.account_type === 5) {
        setUserAdmin(true);
      }
    }
  }, [get_employee, get_auth]);

  // Initialize survey structures and categories
  useEffect(() => {
    if (organizationId) {
      dispatch({
        type: SG_GET_SURVEY_QUESTIONS,
        payload: `survey_type=${SURVEY_QUESTION_TYPE.AUDIT}&status=2`,
      });
      dispatch({
        type: SG_GET_SURVEY_STRUCTURES,
        payload: `organization=${organizationId}`,
      });
    }
  }, [dispatch, organizationId]);

  // Set primary/secondary names from survey structure
  useEffect(() => {
    if (get_survey_structure?.[0]?.categories) {
      setPrimarySecondaryName({
        secondary: get_survey_structure[0].categories.find(
          (f) => f.priority === "secondary"
        )?.name,
        primary: get_survey_structure[0].categories.find(
          (f) => f.priority === "primary"
        )?.name,
      });
    }
  }, [get_survey_structure]);

  // Auto-select survey question: prefer org-selected Audit version, else latest by sort_order, else first
  useEffect(() => {
    if (!Array.isArray(get_survey_questions) || get_survey_questions.length === 0) return;

    // 1) Check organization styling.survey_sequence for an explicit audit_question selection
    const seq = get_auth?.organization?.styling?.survey_sequence?.find(
      (it) => it?.value === "audit_question"
    );
    const seqSortOrder = Number(seq?.question?.sort_order) || null;

    // 2) Fallback to services_enabled → "Audit" → question_sort_order
    const serviceAudit = get_auth?.organization?.services_enabled?.find(
      (s) => s?.name === "Audit"
    );

    const enabledSortOrder = Number(serviceAudit?.question_sort_order) || null;

    const desiredSortOrder = seqSortOrder || enabledSortOrder || null;

    // 3) Try to match by sort_order
    let selected = desiredSortOrder
      ? get_survey_questions.find(
          (sq) => Number(sq?.sort_order) === Number(desiredSortOrder)
        ) || null
      : null;

    // 4) If no match, pick the latest by sort_order (highest value); as a final fallback use first
    if (!selected) {
      selected = [...get_survey_questions]
        .sort((a, b) => Number(b?.sort_order || 0) - Number(a?.sort_order || 0))?.[0]
        || get_survey_questions[0];
    }

    setSelectedSurveyQuestion(selected);
  }, [get_survey_questions, get_auth]);

  // Build category lists from survey structure
  useEffect(() => {
    const _primaryList = [];
    const _secondaryList = [];
    const _groupList = [];

    if (get_survey_structure?.[0]?.categories?.length > 0) {
      get_survey_structure[0].categories.forEach((st) => {
        if (st?.priority === "primary") {
          st.options.forEach((str) => {
            if (str?.subfactors?.length > 0) {
              _groupList.push(str?.name);
              str.subfactors.forEach((opf) => {
                if (userAdmin || userEmployeeCategoryList?.includes(opf.name)) {
                  _primaryList.push({
                    value: opf.name,
                    name: opf?.name,
                    name_id: opf?.id,
                    label: opf?.name,
                    group: str?.name,
                    mainGroup: st?.name,
                    type_of: st?.priority,
                    subtitle: st?.subtitle,
                    priority: st?.priority,
                  });
                }
              });
            } else {
              if (userAdmin || userEmployeeCategoryList?.includes(str.name)) {
                _primaryList.push({
                  value: str?.name,
                  label: str?.name,
                  name: str?.name,
                  name_id: str?.id,
                  type_of: st?.priority,
                  subtitle: st?.subtitle,
                  priority: st?.priority,
                });
              }
            }
          });
        }

        if (st?.priority === "secondary") {
          st.options.forEach((str) => {
            if (userAdmin || userEmployeeCategoryList?.includes(str.name)) {
              _secondaryList.push({
                value: str?.name,
                label: str?.name,
                name: str?.name,
                name_id: str?.id,
                type_of: st?.priority,
                subtitle: st?.subtitle,
                priority: st?.priority,
              });
            }
          });
        }
      });

      setPrimaryCategory(_primaryList);
      setSecondaryCategory(_secondaryList);
      setGroupList(_groupList);
    }
  }, [get_survey_structure, userAdmin, userEmployeeCategoryList]);

  // API Functions for Culture Audit Surveys (Non-Pulse)
  const fetchCultureAuditSchedules = useCallback(
    (status = "pending", page = 1) => {
      if (!organizationId) return;

      setLoading(true);
      // Explicitly request both Not Started (status=1) and Pending (status=2)
      const payload = `page=${page}&type_of=${
        SURVEY_TYPE.AUDIT
      }&organization=${organizationId}${
        selectedPrimary?.value ? `&name=${encodeURIComponent(selectedPrimary?.value?.trim())}` : ""
      }`;

      dispatch({
        type: SG_GET_DEBRIEF_SCHEDULES,
        payload,
      });
    },
    [dispatch, organizationId, selectedPrimary]
  );

  // Fetch historical/completed surveys
  const fetchHistoricalSurveys = useCallback(
    (page = 1) => {
      fetchCultureAuditSchedules("history", page);
    },
    [fetchCultureAuditSchedules]
  );

  // NOTE: We request status=1,2 (Not Started, Pending) server-side,
  // and also apply a client-side guard in getCurrentSurveys in case the API ignores the param.
  // Fetch active/pending surveys
  const fetchActiveSurveys = useCallback(
    (page = 1) => {
      fetchCultureAuditSchedules("pending", page);
    },
    [fetchCultureAuditSchedules]
  );

  // Fetch culture audit reports
  const fetchCultureAuditReports = useCallback(() => {
    if (!organizationId || get_culture_audit_reports?.response) return;

    const payload = ambassadorId
      ? `audit-type-id=${SURVEY_TYPE.AUDIT}&organization=${organizationId}`
      : `audit-type-id=${SURVEY_TYPE.AUDIT}`;

    dispatch({
      type: SG_GET_CULTURE_AUDIT_REPORTS,
      payload,
    });
  }, [dispatch, organizationId, ambassadorId, get_culture_audit_reports]);

  // Delete survey schedule
  const deleteSurveySchedule = useCallback(
    (scheduleId) => {
      if (!scheduleId) return;

      dispatch({
        type: SG_DELETE_DEBRIEF_SCHEDULE,
        payload: { id: scheduleId },
      });
      setDeletedRecord(scheduleId);
      setFetchAgain(true);
    },
    [dispatch]
  );

  // Handle pagination
  const changePage = useCallback(
    (newPage) => {
      setActivePage(newPage);
      dispatch({
        type: ADD_ACTIVE_PAGE_DEBRIEF_SCHEDULE,
        payload: newPage,
      });
    },
    [dispatch]
  );

  // Get total pages
  const getTotalPages = useCallback(() => {
    return Math.ceil(
      (get_debrief_schedules?.debrief_schedule?.count || 0) / defaultPageSize
    );
  }, [get_debrief_schedules]);

  const getCurrentSurveys = useCallback(() => {
    const rows = get_debrief_schedules?.debrief_schedule?.results || [];
    if (!Array.isArray(rows) || rows.length === 0) return [];

    // Include all surveys that are in an actionable/active state for the Active tab
    // Previously this only allowed "pending" and "not started", which caused items to
    // disappear once they flipped to "live" or "processing" after start time.
    const allowedStatuses = new Set(["pending", "not started", "live", "processing", "demo"]);
    const allowedCodes = new Set([1, 2, 3, 4]); // 1=Not Started, 2=Pending, 3=Live, 4=Processing (defensive)

    return rows.filter((s) => {
      const stText = (s?.status_text || "").toLowerCase();
      const stCode = Number(s?.status);
      return allowedStatuses.has(stText) || allowedCodes.has(stCode);
    });
  }, [get_debrief_schedules]);

  // Format survey data for consistent display
  const formatSurveyData = useCallback((surveys) => {
    return surveys.map((survey) => {
      const bucket = debrief_members_by_id?.[survey.id];
      const endpointCount = typeof bucket?.count === 'number'
        ? bucket.count
        : (Array.isArray(bucket?.results) ? bucket.results.length : 0);
      const groupId = survey.organization_group;
      const countFromList = (groupId && typeof membersCountByGroupId[groupId] === 'number')
        ? membersCountByGroupId[groupId]
        : null;
      // Use the authoritative total_requests from survey data
      const respondentsCount = survey.total_requests || 0;

      // Calculate accurate response count based on survey_status = 3 (Completed/Submitted)
      const calculateResponseCount = () => {
        const compliance = Array.isArray(survey.compliance) ? survey.compliance : [];
        
        // Prefer compliance data when available (most accurate)
        if (compliance.length > 0) {
          return compliance.length;
        }
        
        // Fall back to total_responses from survey summary
        return survey.total_responses || 0;
      };

      const responseCount = calculateResponseCount();

      // Find the survey structure name
      const surveyStructureId = survey.survey_structure;
      const surveyStructure = Array.isArray(get_survey_structure) 
        ? get_survey_structure.find(structure => structure.id === surveyStructureId)
        : null;
      const surveyStructureName = surveyStructure?.name || "Culture Audit";
      const scheduleName = typeof survey.name === 'string' ? survey.name.trim() : '';

      return {
        id: survey.id,
        name: scheduleName,
        title: survey.name,
        type: "Culture Audit", // Keep this for the type badge
        surveyStructureName: surveyStructureName, // Add the actual survey structure name
        status: survey.status_text?.toLowerCase() || "pending",
        startDate: survey.scheduled_for,
        endDate: survey.close_at,
        participants: respondentsCount,
        responses: responseCount,
        completionRate:
          respondentsCount > 0
            ? `${Math.round((responseCount / respondentsCount) * 100)}%`
            : "0%",
        statusText: survey.status_text,
        rawData: survey,
        updatedAt: formatUpdatedDisplayDate(survey.updated_at),
        updatedValue: survey.updated_at,
      };
    });
  }, [debrief_members_by_id, membersCountByGroupId, get_survey_structure]);
  // Fetch respondent members per debrief schedule (uses dedicated endpoint)
  useEffect(() => {
    const results = get_debrief_schedules?.debrief_schedule?.results;
    if (!Array.isArray(results) || results.length === 0) return;
    results.forEach((s) => {
      const id = s?.id;
      const groupId = s?.organization_group;
      if (!id) return;
      const cachedBucket = debrief_members_by_id?.[id];
      const haveCountFromList = groupId && typeof membersCountByGroupId[groupId] === 'number';
      const needDetail = !cachedBucket || !Array.isArray(cachedBucket.results); // only fetch when we need actual rows
      if (!haveCountFromList && needDetail) {
        // default filter: include active/queued/completed tokens and responses
        const defaultQuery = 'page=1&page_size=500&brief_token_status=2,1&debrief_response_status=2,4';
        dispatch({ type: SG_GET_DEBRIEF_MEMBERS, payload: { id, query: defaultQuery } });
      }
    });
  }, [
    dispatch,
    get_debrief_schedules?.debrief_schedule?.results,
    debrief_members_by_id,
    membersCountByGroupId
  ]);

  // Clear errors and reset states
  const clearErrors = useCallback(() => {
    setError(null);
    setFetchAgain(false);
    setDeletedRecord(false);
  }, []);

  // Refresh data
  const refreshData = useCallback(() => {
    setFetchAgain(true);
    if (organizationId) {
      fetchActiveSurveys(activePage);
      fetchCultureAuditReports();
    }
  }, [
    organizationId,
    activePage,
    fetchActiveSurveys,
    fetchCultureAuditReports,
  ]);

  // Auto-fetch data when dependencies change
  useEffect(() => {
    if (organizationId && activePage) {
      fetchActiveSurveys(activePage);
    }
  }, [
    organizationId,
    activePage,
    fetchAgain,
    deletedRecord,
    selectedPrimary,
    fetchActiveSurveys,
  ]);

  useEffect(() => {
    fetchCultureAuditReports();
  }, [fetchCultureAuditReports]);

  // Handle loading states
  useEffect(() => {
    if (fetchAgain || deletedRecord) {
      setLoading(true);
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [fetchAgain, deletedRecord]);

  // Start the clean 3-step flow from any component
  const scheduleSurveyWithGroup = ({
    formData,
    recipientMode,
    selectedGroupId,
    selectedEmployees = [],
    groupName = "",
  }) => {
    if (!organizationId) {
      setScheduleFlow({
        active: false,
        step: "error",
        createdGroupId: null,
        error: new Error("Missing organization"),
        temp: null,
      });
      return;
    }

    // Existing group → schedule immediately
    if (recipientMode === "existing" && selectedGroupId) {
      const payload = buildSchedulePayload({
        formData,
        groupId: selectedGroupId,
      });
      setScheduleFlow({
        active: true,
        step: "scheduling",
        createdGroupId: selectedGroupId,
        error: null,
        temp: { formData, recipientMode },
      });
      dispatch({ type: SG_ADD_DEBRIEF_SCHEDULE, payload });
      setScheduleFlow((prev) => ({ ...prev, step: "done", active: false }));
      return;
    }

    // New group → create → add members → schedule
    const name = (
      groupName || `Survey Group – ${new Date().toLocaleString()}`
    ).trim();
    const groupPayload = {
      name,
      description: "",
      organization: organizationId,
      group_type: "survey", // ✅ confirm backend literal
    };

    setScheduleFlow({
      active: true,
      step: "creatingGroup",
      createdGroupId: null,
      error: null,
      temp: { formData, selectedEmployees, groupName: name, recipientMode },
    });

    dispatch({ type: SG_ADD_ORGANIZATION_GROUP, payload: groupPayload });
  };

  // Gated orchestration - wait for success signals before proceeding
  useEffect(() => {
    if (!scheduleFlow.active) return;

    // Step 1: Group created → start bulk update
    if (scheduleFlow.step === "creatingGroup") {
      const newId = organization_group?.id;
      if (!newId) return; // wait until reducer exposes the id

      setScheduleFlow((prev) => ({
        ...prev,
        step: "waitingBulk",
        createdGroupId: newId,
      }));

      // Always bulk add (even for small sets) using the new bulk_update_members endpoint
      // This will add members to the group but NOT create tokens (since survey isn't live yet)
      if (
        Array.isArray(scheduleFlow.temp?.selectedEmployees) &&
        scheduleFlow.temp.selectedEmployees.length > 0
      ) {
        dispatch({
          type: SG_BULK_UPDATE_ORGANIZATION_GROUP,
          payload: {
            organization_group_id: newId,
            employee_ids: scheduleFlow.temp.selectedEmployees,
            action: "add",
            create_tokens: false, // Don't create tokens during survey creation
            send_email: false,    // Don't send emails during survey creation
          },
        });
      } else {
        // No employees to add, proceed directly to scheduling
        setScheduleFlow((prev) => ({ ...prev, step: "waitingSchedule" }));
        const payload = buildSchedulePayload({
          formData: scheduleFlow.temp.formData,
          groupId: newId,
        });
        dispatch({ type: SG_ADD_DEBRIEF_SCHEDULE, payload });
      }
    }

    // Step 2: Bulk update completed → start scheduling
    if (scheduleFlow.step === "waitingBulk") {
      const bulkCompleted = 
        stateOrgGroup.last_action === 'BULK' && 
        stateOrgGroup.lastAffectedGroupId === scheduleFlow.createdGroupId;
      
      if (bulkCompleted) {
        setScheduleFlow((prev) => ({ ...prev, step: "waitingSchedule" }));
        const payload = buildSchedulePayload({
          formData: scheduleFlow.temp.formData,
          groupId: scheduleFlow.createdGroupId,
        });
        dispatch({ type: SG_ADD_DEBRIEF_SCHEDULE, payload });
      }
    }

    // Step 3: Schedule completed → finish 
    if (scheduleFlow.step === "waitingSchedule") {
      // Watch for successful schedule creation in debrief_schedule state
      if (get_debrief_schedules?.created && get_debrief_schedules?.debrief_id) {
        const scheduleCreated = get_debrief_schedules.created;
        const currentTime = Date.now();
        
        // Ensure this is a new schedule creation (avoid false positives from old state)
        if (!lastScheduleCreationTime || currentTime > lastScheduleCreationTime) {
          setLastScheduleCreationTime(currentTime);
          setScheduleFlow((prev) => ({ ...prev, step: "done", active: false }));
        }
      }
    }
  }, [
    scheduleFlow.active, 
    scheduleFlow.step, 
    scheduleFlow.createdGroupId,
    scheduleFlow.temp,
    organization_group?.id,
    stateOrgGroup.last_action,
    stateOrgGroup.lastAffectedGroupId,
    get_debrief_schedules?.created,
    get_debrief_schedules?.debrief_id,
    lastScheduleCreationTime,
    dispatch,
    buildSchedulePayload
  ]);

  // Context value
  const value = {
    // State
    loading,
    error,
    activePage,
    organizationId,
    userAdmin,
    userEmployee,
    fetchAgain,
    deletedRecord,

    // Survey structure data
    primaryCategory,
    secondaryCategory,
    groupList,
    primarySecondaryName,
    selectedPrimary,
    setSelectedPrimary,
    selectedSurveyQuestion,

    // Redux data
    get_survey_questions,
    get_survey_structure,
    get_organizations,
    get_debrief_schedules,
    get_culture_audit_reports,

    // API functions
    fetchActiveSurveys,
    fetchHistoricalSurveys,
    fetchCultureAuditReports,
    deleteSurveySchedule,
    changePage,
    refreshData,
    clearErrors,

    // Data helpers
    getCurrentSurveys,
    formatSurveyData,
    getTotalPages,

    // Pagination
    defaultPageSize,

    // Survey types
    SURVEY_TYPE,
    SURVEY_INFO,

    // Orchestrated scheduling API/state
    scheduleSurveyWithGroup,
    scheduleFlow,

    // Respondents helpers
    getRespondentsForSchedule,
    groupMembersById,
    debrief_members_by_id,
    membersCountByGroupId,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};

export default ScheduleContext;
